import { useApolloClient } from "@apollo/client";
import { filterNonNull } from "@msys/common";
import { pick, uniq } from "lodash-es";
import React from "react";
import { ItemType } from "../../../clients/graphqlTypes.js";
import { QUOTE_ITEM_TYPES } from "../../constants.js";
import { isAllowedToPaste } from "../doc-items/constraints.js";
import {
  CreateItemFromTemplateArgs,
  CreateItemsFromTemplatesArgs,
  type CreateItemsReturn,
  type CreateItemsVariables,
  CreateItemsWithProductsArgs,
  useCreateItem,
} from "../doc-items/hooks/useCreateItem.js";
import { useDocItemsClipboard } from "../doc-items/hooks/useDocItemsClipboard.js";
import { useAvailabilityWarning } from "./alerts/QuoteProductsAvailabilityAlert.js";
import {
  useCreateQuoteItems_CreateItemsFromTemplatesMutation,
  useCreateQuoteItems_CreateItemsMutation,
  useCreateQuoteItems_PasteItemFromClipboardMutation,
} from "./useCreateQuoteItems.generated.js";

export function useCreateQuoteItems({
  projectId,
  quoteId,
  expandedItemIds,
  onItemCreated,
  source,
}: {
  projectId: string;
  quoteId: string;
  expandedItemIds: string[] | undefined;
  onItemCreated?(): Promise<void> | void; // should be memoized
  source: "edit" | "calculation" | "3d" | "grid";
}) {
  const doProductAvailabilityWarning = useAvailabilityWarning();

  const client = useApolloClient();
  const [createItems] = useCreateQuoteItems_CreateItemsMutation({ client });

  const createDocumentItems = React.useCallback(
    async (args: {
      variables: CreateItemsVariables;
    }): Promise<CreateItemsReturn> => {
      return await createItems({
        ...args,
        variables: {
          ...args.variables,
          isEdit: source === "edit",
          isCalculation: source === "calculation",
          is3d: source === "3d",
          isDataGrid: source === "grid",
        },
      });
    },
    [createItems, source]
  );

  const { createItem, createItemByType } = useCreateItem({
    projectId,
    createDocumentItems,
    expandedItemIds,
    onItemCreated,
  });

  const [createItemsFromTemplatesMutation] =
    useCreateQuoteItems_CreateItemsFromTemplatesMutation({
      client,
    });

  const createItemFromTemplate = React.useCallback(
    async ({
      parentItemId,
      templateQuoteId,
      newItemRank,
    }: CreateItemFromTemplateArgs) => {
      const result = await createItemsFromTemplatesMutation({
        variables: {
          input: {
            parentProjectId: projectId,
            parentDocId: quoteId,
            parentItemId,
            templates: [{ templateQuoteId }],
            expandedItemIds,
            rank: newItemRank,
          },
          isEdit: source === "edit",
          isCalculation: source === "calculation",
          is3d: source === "3d",
          isDataGrid: source === "grid",
        },
      });
      await onItemCreated?.();
      return result.data?.createItemsFromTemplates.items[0];
    },
    [
      createItemsFromTemplatesMutation,
      expandedItemIds,
      onItemCreated,
      projectId,
      quoteId,
      source,
    ]
  );

  const createItemsFromTemplates = React.useCallback(
    async ({
      parentItemId,
      templates,
      newItemRank,
    }: CreateItemsFromTemplatesArgs) => {
      const result = await createItemsFromTemplatesMutation({
        variables: {
          input: {
            parentProjectId: projectId,
            parentDocId: quoteId,
            parentItemId,
            templates: templates.map(tpl => ({
              templateQuoteId: tpl.template.id,
              templateEstimatedQuantity: tpl.quantity,
            })),
            expandedItemIds,
            rank: newItemRank,
          },
          isEdit: source === "edit",
          isCalculation: source === "calculation",
          is3d: source === "3d",
          isDataGrid: source === "grid",
        },
      });
      await onItemCreated?.();
      return result.data?.createItemsFromTemplates.items;
    },
    [
      createItemsFromTemplatesMutation,
      expandedItemIds,
      onItemCreated,
      projectId,
      quoteId,
      source,
    ]
  );

  const createItemsWithProducts = React.useCallback(
    async ({
      parentItemId,
      products,
      newItemRank,
    }: CreateItemsWithProductsArgs) => {
      const result = await createItems({
        variables: {
          input: {
            projectId,
            docId: quoteId,
            parentItemId,
            items: products.map(opts =>
              "__typename" in opts.product
                ? {
                    title: opts.product.texts?.title ?? "",
                    type: "paid",
                    decisionBehaviorOfSubitems: "NONE",
                    estimatedQuantity: opts.quantity,
                    product: pick(opts.product, "supplierId", "articleNumber"),
                  }
                : {
                    title: opts.product.productTitle ?? "",
                    type: "paid",
                    decisionBehaviorOfSubitems: "NONE",
                    estimatedQuantity: opts.quantity,
                    productValues: opts.product,
                  }
            ),
            expandedItemIds,
            rank: newItemRank,
          },
          isEdit: source === "edit",
          isCalculation: source === "calculation",
          is3d: source === "3d",
          isDataGrid: source === "grid",
        },
      });

      const setProducts = filterNonNull(
        products.map(p => ("__typename" in p.product ? p.product : null))
      );

      const setProductsStatusWithAvailabilityWarning = uniq(
        filterNonNull(
          setProducts.map(p =>
            p.status && p.status.value !== "available" ? p.status.value : null
          )
        )
      );

      for (const status of setProductsStatusWithAvailabilityWarning) {
        doProductAvailabilityWarning(status);
      }

      await onItemCreated?.();

      return result.data?.createItems.items;
    },
    [
      createItems,
      projectId,
      quoteId,
      expandedItemIds,
      source,
      onItemCreated,
      doProductAvailabilityWarning,
    ]
  );

  const [copiedItem] = useDocItemsClipboard();
  const [pasteItemFromClipboardMutation] =
    useCreateQuoteItems_PasteItemFromClipboardMutation({ client });
  const pasteItem = React.useCallback(
    async (docId: string, parentItemId: string) => {
      if (!copiedItem) throw new Error("No item copied");
      const result = await pasteItemFromClipboardMutation({
        variables: {
          input: {
            sourceProjectId: copiedItem.projectId,
            sourceDocId: copiedItem.docId,
            sourceItemId: copiedItem.itemId,
            destProjectId: projectId,
            destDocId: docId,
            destItemId: parentItemId,
            expandedItemIds,
          },
          isEdit: source === "edit",
          isCalculation: source === "calculation",
          is3d: source === "3d",
          isDataGrid: source === "grid",
        },
      });
      await onItemCreated?.();
    },
    [
      copiedItem,
      pasteItemFromClipboardMutation,
      projectId,
      expandedItemIds,
      source,
      onItemCreated,
    ]
  );
  const isAllowedToPasteItem = React.useCallback(
    (parentItem: { type: ItemType }) => {
      if (!copiedItem?.itemType || !parentItem.type) return false;

      return isAllowedToPaste({
        documentItemTypes: QUOTE_ITEM_TYPES,
        copiedItemType: copiedItem.itemType,
        parentItemType: parentItem.type,
      });
    },
    [copiedItem?.itemType]
  );

  return {
    createItem,
    createItemByType,
    createItemFromTemplate,
    createItemsFromTemplates,
    createItemsWithProducts,
    pasteItem,
    isAllowedToPasteItem,
  };
}
